import { Sprite } from "pixi.js";

const cache = new WeakMap();

export async function findInnerHole(texture, renderer, { alphaThreshold = 10 } = {}) {
    const cached = cache.get(texture);
    if (cached) return cached;

    const { data, width, height, pixelScale } = await readPixels(texture, renderer);
    const px = bfsHoleFromCenter(data, width, height, alphaThreshold);
    const rect = {
        x: px.x / pixelScale,
        y: px.y / pixelScale,
        w: px.w / pixelScale,
        h: px.h / pixelScale,
    };
    cache.set(texture, rect);
    return rect;
}

async function readPixels(texture, renderer) {
    // Универсальный путь через renderer.extract: учитывает texture.rotate
    // (TexturePacker может вращать спрайт в атласе), trim/orig и т.п.
    if (renderer?.extract) {
        const sprite = new Sprite(texture);
        try {
            const result = await renderer.extract.pixels({ target: sprite });
            // result.* — в физических пикселях (DPR), нормализуем к логическим.
            const logicalW = sprite.width || texture.width || result.width;
            const pixelScale = logicalW > 0 ? result.width / logicalW : 1;
            return { data: result.pixels, width: result.width, height: result.height, pixelScale };
        } finally {
            sprite.destroy();
        }
    }

    // Fallback на CPU. Не работает с повёрнутыми/trim'нутыми атласами.
    if (texture?.rotate) {
        throw new Error("findInnerHole: rotated atlas frame requires a renderer");
    }

    const src = texture?.source?.resource;
    const frame = texture?.frame;
    const fx = frame?.x ?? 0;
    const fy = frame?.y ?? 0;
    const fw = frame?.width ?? texture?.width ?? src?.width ?? src?.naturalWidth ?? 0;
    const fh = frame?.height ?? texture?.height ?? src?.height ?? src?.naturalHeight ?? 0;

    const isImageLike =
        (typeof HTMLImageElement !== "undefined" && src instanceof HTMLImageElement) ||
        (typeof HTMLCanvasElement !== "undefined" && src instanceof HTMLCanvasElement) ||
        (typeof ImageBitmap !== "undefined" && src instanceof ImageBitmap);

    if (!isImageLike || fw <= 0 || fh <= 0) {
        throw new Error("findInnerHole: cannot read pixels — pass a renderer");
    }

    const canvas =
        typeof OffscreenCanvas !== "undefined"
            ? new OffscreenCanvas(fw, fh)
            : Object.assign(document.createElement("canvas"), { width: fw, height: fh });
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(src, fx, fy, fw, fh, 0, 0, fw, fh);
    const img = ctx.getImageData(0, 0, fw, fh);
    return { data: img.data, width: fw, height: fh, pixelScale: 1 };
}

function bfsHoleFromCenter(data, W, H, t) {
    const isTransparent = (x, y) => data[(y * W + x) * 4 + 3] < t;
    let cx = W >> 1;
    let cy = H >> 1;

    if (!isTransparent(cx, cy)) {
        const found = findNearestTransparent(data, W, H, cx, cy, t);
        if (!found) throw new Error("findInnerHole: no transparent area found");
        [cx, cy] = found;
    }

    const seen = new Uint8Array(W * H);
    const stack = [cx, cy];
    seen[cy * W + cx] = 1;
    let minX = cx, maxX = cx, minY = cy, maxY = cy;

    while (stack.length) {
        const y = stack.pop();
        const x = stack.pop();
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;

        if (x + 1 < W) {
            const i = y * W + (x + 1);
            if (!seen[i] && isTransparent(x + 1, y)) { seen[i] = 1; stack.push(x + 1, y); }
        }
        if (x > 0) {
            const i = y * W + (x - 1);
            if (!seen[i] && isTransparent(x - 1, y)) { seen[i] = 1; stack.push(x - 1, y); }
        }
        if (y + 1 < H) {
            const i = (y + 1) * W + x;
            if (!seen[i] && isTransparent(x, y + 1)) { seen[i] = 1; stack.push(x, y + 1); }
        }
        if (y > 0) {
            const i = (y - 1) * W + x;
            if (!seen[i] && isTransparent(x, y - 1)) { seen[i] = 1; stack.push(x, y - 1); }
        }
    }

    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

function findNearestTransparent(data, W, H, cx, cy, t) {
    const maxR = Math.max(W, H);
    for (let r = 1; r < maxR; r++) {
        for (let dy = -r; dy <= r; dy++) {
            for (let dx = -r; dx <= r; dx++) {
                if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
                const x = cx + dx;
                const y = cy + dy;
                if (x < 0 || y < 0 || x >= W || y >= H) continue;
                if (data[(y * W + x) * 4 + 3] < t) return [x, y];
            }
        }
    }
    return null;
}
